// Canonical feature-grant keys for the Partner Program pilot surfaces (PTEC-557).
// Read at runtime via organisation.grants.features — grants are named by
// capability, not surface (see Partner Program — Grants & Entitlements).

/** Feature value gating Lead-Share (PTEC-512). Partner-origin. */
export const LEAD_SHARE_FEATURE = 'leadShare';

/**
 * Capability-level feature flag for Lead-Share (PTEC-515). Default off: the
 * capability ships dark and transmits nothing until an app admin enables it
 * after compliance sign-off. Distinct from the per-org LEAD_SHARE_FEATURE
 * grant — both must be on for the surface to activate.
 */
export const PARTNER_LEADSHARE_FLAG = 'partner-leadshare-enabled';

/**
 * Capability-level feature flag for the Partner Program surfaces (PTEC-730).
 * Default off, and locked off on the live environments for v3.15.0
 * on the same footing as PARTNER_LEADSHARE_FLAG: the org-facing screen reads
 * mocked enrolment and grant data, so an org admin in demo or production would
 * be shown an invented partner status, tier and lead volume. Delivery is
 * deferred to v3.16.0 with the enrolment backend.
 *
 * Read at two gates that must agree: the sidebar entry (SidebarMenu) and the
 * route guard (router/organisation), so the URL is closed as well as the nav.
 */
export const PARTNER_PROGRAM_FLAG = 'partner-program-enabled';

/** Feature value gating Prospects (PTEC-546). Partner-origin in v1 (lead-view scope). */
export const PROSPECTS_FEATURE = 'prospects';

/**
 * Feature value gating the Organisation Constituents roster. Controls the
 * dashboard, the upload endpoints, and the post-confirmation linking surface.
 */
export const CONSTITUENTS_FEATURE = 'constituents';

/**
 * Feature value gating partner API provisioning. Enforced where credentials are
 * ISSUED (app-client creation), never on live API traffic — revoking the grant
 * must not break an integration that is already running.
 */
export const API_ACCESS_FEATURE = 'apiAccess';

/**
 * Feature value gating organisation-customised, co-branded emails (PTEC-895):
 * Org Admin edits of the invitation emails, their use at send time, and the
 * co-branded header. Independent of `partnerBranding`, which only decides which
 * logo is preferred. The platform switch is CUSTOM_EMAILS_FLAG.
 */
export const CUSTOM_EMAILS_FEATURE = 'customEmails';

// The capabilities offered at partner enrolment (PTEC-549). This is a SUBSET of
// PARTNER_GRANT_FEATURES in the admins Lambda (service.js), which additionally
// accepts `apiAccess`; `constituents` is in neither. Both are grantable only via
// the per-org feature toggles, not at enrolment time. Membership is pending
// ratification on PTEC-439 — keep the backend list authoritative and widen this
// one only with that decision, since the backend rejects unknown features.
export const PARTNER_GRANT_CATALOGUE: { key: string; label: string; hint: string }[] = [
	{ key: 'Slate', label: 'Slate', hint: 'Slate document requests and evaluations' },
	{ key: 'Portal', label: 'Portal', hint: 'Educational portal search and profiles' },
	{ key: 'LeadShare', label: 'Lead-Share', hint: 'Consented lead transmission to the partner' },
	{ key: 'Prospects', label: 'Prospects', hint: 'Prospect discovery and outreach' },
	{ key: 'partnerBranding', label: 'Partner branding', hint: 'Co-branded consent and sharing surfaces' },
	{ key: 'customEmails', label: 'Custom emails', hint: 'Org-customised, co-branded invitation emails' }
];

/**
 * Canonical catalogue of the profile fields a partner Lead-Share enrollment may
 * transmit (PTEC-516). Mirrors the backend allow-list in the admins Lambda
 * (leadShareEnrollment.js). Which fields actually transmit for a partner is a
 * per-partner, legal-signed-off decision (data-minimisation, decision D3).
 * Most-recent-school is derived from educationHistory at delivery time.
 */
export const LEAD_SHARE_FIELD_CATALOGUE: { key: string; label: string }[] = [
	{ key: 'firstName', label: 'First name' },
	{ key: 'lastName', label: 'Last name' },
	{ key: 'email', label: 'Email' },
	{ key: 'phone', label: 'Phone' },
	{ key: 'dateOfBirth', label: 'Date of birth' },
	{ key: 'country', label: 'Country' },
	{ key: 'educationHistory', label: 'Education history' }
];

/**
 * Interests a user can self-identify into for Lead-Share audiences (PTEC-568).
 * Picked, never typed. Display copy of the canonical backend catalogue in
 * `inspirusLayer/lib/nodejs/leadShareInterestCatalogue.js` (PTEC-573) — a
 * browser bundle cannot import a Lambda layer, so the keys are duplicated here
 * by necessity and drift-guarded by the admins suite's
 * `interestCatalogueSync.test.js` (membership AND order must match).
 */
export const LEAD_SHARE_INTEREST_CATALOGUE: { key: string; label: string }[] = [
	{ key: 'additional-needs-support', label: 'Additional-needs support' },
	{ key: 'open-days', label: 'Open days & campus events' },
	{ key: 'scholarships-funding', label: 'Scholarships & funding' },
	{ key: 'course-information', label: 'Course information' },
	{ key: 'career-opportunities', label: 'Career opportunities' }
];

/**
 * Case-insensitive membership check, matching how existing feature gates treat
 * mixed-cased authored values (see `hasPartnerBrandingFeature` and the slate gate).
 */
export const hasFeatureGrant = (features: readonly string[] | null | undefined, feature: string): boolean =>
	Array.isArray(features) && features.some((f) => String(f).toLowerCase() === feature.toLowerCase());
